import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  QrCode2 as QrCode2Icon,
  CallMade as CallMadeIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { ethereumService } from '../utils/ethereum';
import { ethers } from 'ethers';
import { useAuth } from '@clerk/clerk-react';
import { useProject } from '../projects/ProjectContext';
import { defaultConfig, getConfig } from '../config/bundlerConfig';
import { addRecoverySnapshot } from '../utils/recoveryBackup';
import { QRCodeCanvas } from 'qrcode.react';

const MotionPaper = motion(Paper);

const pillSx = {
  borderRadius: 999,
  px: 2.25,
  fontWeight: 700,
  textTransform: 'none',
};

const gradientPrimarySx = {
  ...pillSx,
  backgroundImage:
    'linear-gradient(90deg, rgba(168, 85, 247, 1) 0%, rgba(99, 102, 241, 1) 45%, rgba(34, 211, 238, 1) 100%)',
  color: '#08090e',
  boxShadow: '0 8px 28px rgba(168, 85, 247, 0.28)',
  '&:hover': {
    backgroundImage:
      'linear-gradient(90deg, rgba(187, 110, 255, 1) 0%, rgba(115, 122, 255, 1) 45%, rgba(56, 225, 255, 1) 100%)',
    boxShadow: '0 10px 32px rgba(168, 85, 247, 0.35)',
  },
  '&.Mui-disabled': { backgroundImage: 'none', color: alpha('#e9e7ff', 0.45) },
};

const isCreationWallet = (w) => w?._kind === 'creation';

/** Bundle wallets expose `privateKey` from API; creation row uses `/api/me/creation-wallet/export`. */
const walletCanExportPrivateKey = (w) => {
  if (!w?.address) return false;
  if (isCreationWallet(w)) return true;
  return !!(w.privateKey && String(w.privateKey).trim());
};

const normalizePrivateKeyHex = (pk) => {
  const s = (pk || '').toString().trim();
  if (!s) return '';
  return s.startsWith('0x') ? s : `0x${s}`;
};

/** Skip sell-all for wallets with no project token (uses row tokenBalance from last refresh). */
const hasPositiveTokenBalance = (w) => {
  const raw = w?.tokenBalance;
  if (raw === undefined || raw === null) return false;
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw).trim().replace(/,/g, ''));
  return Number.isFinite(n) && n > 0;
};

const WalletManagement = () => {
  const { isSignedIn, getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);
  const { currentProjectId, projectSettings } = useProject();
  const projectTokenAddress = useMemo(
    () => (projectSettings?.token_address || projectSettings?.tokenAddress || '').toString().trim(),
    [projectSettings],
  );
  const theme = useTheme();
  const accentPurple = theme.palette.primary.main;
  const accentCyan = theme.palette.secondary.main;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSystem, setLoadingSystem] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [sellingAll, setSellingAll] = useState(false);
  const [backingUpMasterKey, setBackingUpMasterKey] = useState(false);
  const [needsMasterWallet, setNeedsMasterWallet] = useState(false);
  const [sellAllPercent, setSellAllPercent] = useState('0');
  const [systemWallets, setSystemWallets] = useState({
    master: { address: '', balance: '0' },
    funding: { address: '', balance: '0' },
  });
  const [confirmState, setConfirmState] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);
  const [transferState, setTransferState] = useState(null);
  const [sellState, setSellState] = useState(null);
  const [sendEthState, setSendEthState] = useState(null);
  const [creationWalletRow, setCreationWalletRow] = useState(null);
  const [masterQrOpen, setMasterQrOpen] = useState(false);
  const [masterSendTo, setMasterSendTo] = useState('');
  const [masterSendAmount, setMasterSendAmount] = useState('');
  const [masterSending, setMasterSending] = useState(false);
  const [masterMaxing, setMasterMaxing] = useState(false);
  const [privateKeyExport, setPrivateKeyExport] = useState(null);

  const apiFetch = useCallback(async (path, init = {}) => {
    const token = await getTokenRef.current();
    const res = await fetch(`${defaultConfig.SERVER_URL.url}${path}`, {
      ...init,
      headers: {
        ...(init.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(currentProjectId ? { 'X-Project-Id': currentProjectId } : {}),
      },
    });
    const text = await res.text();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = {};
    }
    if (!res.ok) {
      const msg = data?.error
        ? data.error
        : `Request failed (${res.status} ${res.statusText})${text ? `: ${text.slice(0, 200)}` : ''}`;
      throw new Error(msg);
    }
    return data;
  }, [currentProjectId]);

  const backupMasterKeyToRecovery = useCallback(async ({ silent = false } = {}) => {
    setBackingUpMasterKey(true);
    try {
      const exported = await apiFetch('/api/me/master-wallet/export');
      if (!exported?.privateKey) throw new Error('No private key returned');
      addRecoverySnapshot({
        projectId: currentProjectId,
        type: 'masterWallet',
        label: 'Master wallet exported (auto)',
        data: { address: exported.address, privateKey: exported.privateKey },
      });
      if (!silent) toast.success('Master key saved to Recovery → Auto backups');

      try {
        const addr = (exported.address || '').toLowerCase();
        const pid = (currentProjectId || '').toString().trim();
        if (addr) localStorage.setItem(`recovery.masterKey.backedUp.${pid || 'global'}.${addr}`, new Date().toISOString());
      } catch {
        // ignore
      }
    } catch (e) {
      if (!silent) toast.error(e?.message || 'Failed to export master key');
    } finally {
      setBackingUpMasterKey(false);
    }
  }, [apiFetch]);

  // Automatically back up master key once per address.
  useEffect(() => {
    const addr = (systemWallets.master.address || '').toLowerCase();
    if (!addr) return;
    if (needsMasterWallet) return;
    if (!currentProjectId) return;

    try {
      const pid = (currentProjectId || '').toString().trim();
      const already = localStorage.getItem(`recovery.masterKey.backedUp.${pid || 'global'}.${addr}`);
      if (already) return;
    } catch {
      // if localStorage fails, still attempt once per mount
    }

    // Fire-and-forget, silent.
    backupMasterKeyToRecovery({ silent: true });
  }, [backupMasterKeyToRecovery, currentProjectId, needsMasterWallet, systemWallets.master.address]);

  useEffect(() => {
    if (!isSignedIn) return;
    loadWallets();
    loadSystemWallets();
    loadCreationWalletRow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, currentProjectId, projectTokenAddress]);

  const loadSystemWallets = async () => {
    setLoadingSystem(true);
    try {
      await ethereumService.ensureInitialized();
      setNeedsMasterWallet(false);
      const masterAddr = ethereumService.masterWallet.address;
      const fundingAddr = ethereumService.fundingWallet.address;
      // Funding currently aliases master; if we ever split them this still
      // makes only the calls we actually need.
      const sameAddr = masterAddr.toLowerCase() === fundingAddr.toLowerCase();
      const [masterBalance, fundingBalance] = sameAddr
        ? await ethereumService.provider.getBalance(masterAddr).then((b) => [b, b])
        : await Promise.all([
            ethereumService.provider.getBalance(masterAddr),
            ethereumService.provider.getBalance(fundingAddr),
          ]);

      setSystemWallets({
        master: {
          address: masterAddr,
          balance: ethers.utils.formatEther(masterBalance)
        },
        funding: {
          address: fundingAddr,
          balance: ethers.utils.formatEther(fundingBalance)
        }
      });
    } catch (error) {
      console.error('Failed to load system wallets:', error);
      const msg = typeof error === 'string' ? error : (error?.message || String(error));
      if (msg.includes('EthereumService not initialized')) {
        setNeedsMasterWallet(true);
        return;
      }
      toast.error(`Failed to load system wallet balances: ${msg}`);
    } finally {
      setLoadingSystem(false);
    }
  };

  const handleOpenMasterQr = () => {
    setMasterSendTo('');
    setMasterSendAmount('');
    setMasterQrOpen(true);
  };

  const masterSendToOk = useMemo(() => {
    const to = masterSendTo.trim();
    return !to ? null : ethers.utils.isAddress(to);
  }, [masterSendTo]);

  const masterSendAmountOk = useMemo(() => {
    const amt = masterSendAmount.trim();
    if (!amt) return null;
    const n = Number(amt);
    return Number.isFinite(n) && n > 0;
  }, [masterSendAmount]);

  const handleSendFromMaster = async () => {
    const from = systemWallets.master.address;
    const to = masterSendTo.trim();
    const amt = masterSendAmount.trim();
    if (!from) return toast.error('Fund wallet is not set');
    if (!ethers.utils.isAddress(to)) return toast.error('Destination address is invalid');
    const n = Number(amt);
    if (!Number.isFinite(n) || n <= 0) return toast.error('Amount must be > 0');

    setMasterSending(true);
    try {
      await ethereumService.ensureInitialized();
      const value = ethers.utils.parseEther(amt);
      const provider = ethereumService.provider;
      const fee = await provider.getFeeData().catch(() => null);
      const network = await provider.getNetwork();
      const nonce = await ethereumService.masterWallet.getTransactionCount('pending');

      const use1559 = !!fee?.maxFeePerGas;
      const txReq = {
        to,
        value,
        gasLimit: 21000,
        chainId: network.chainId,
        nonce,
        ...(use1559 ? { type: 2 } : { type: 0 }),
        ...(use1559 ? { maxFeePerGas: fee.maxFeePerGas } : {}),
        ...(use1559 && fee?.maxPriorityFeePerGas ? { maxPriorityFeePerGas: fee.maxPriorityFeePerGas } : {}),
        ...(!use1559 && fee?.gasPrice ? { gasPrice: fee.gasPrice } : {}),
      };

      const signedTx = await ethereumService.masterWallet.signTransaction(txReq);
      const relayRes = await ethereumService.sendSignedTransactionsViaFlashbots([signedTx]);
      const hash = relayRes?.result || relayRes?.bundleHash || relayRes?.hash || null;
      toast.success(hash ? `Submitted via Flashbots: ${String(hash).slice(0, 12)}…` : 'Submitted via Flashbots');
      setMasterQrOpen(false);
      await refreshAllBalances();
    } catch (e) {
      toast.error(e?.message || 'Transfer failed');
    } finally {
      setMasterSending(false);
    }
  };

  const handleMasterMax = async () => {
    if (!systemWallets.master.address) return;
    setMasterMaxing(true);
    try {
      await ethereumService.ensureInitialized();
      const provider = ethereumService.provider;
      const fee = await provider.getFeeData().catch(() => null);
      const bal = await provider.getBalance(systemWallets.master.address);
      const gasLimit = ethers.BigNumber.from(21000);
      const gasPrice = fee?.maxFeePerGas || fee?.gasPrice || ethers.utils.parseUnits('2', 'gwei');
      const gasCost = gasLimit.mul(gasPrice);
      const maxWei = bal.gt(gasCost) ? bal.sub(gasCost) : ethers.BigNumber.from(0);
      const maxEth = ethers.utils.formatEther(maxWei);
      // keep a tiny safety buffer
      const buffered = Math.max(0, Number(maxEth) - 0.00001);
      setMasterSendAmount(buffered.toFixed(6));
    } catch (e) {
      toast.error(e?.message || 'Failed to calculate max');
    } finally {
      setMasterMaxing(false);
    }
  };

  const loadWallets = async () => {
    setLoading(true);
    try {
      const loadedWallets = await ethereumService.loadWallets();
      setNeedsMasterWallet(false);
      setWallets(loadedWallets);
    } catch (error) {
      const msg = typeof error === 'string' ? error : (error?.message || '');
      if (msg.includes('EthereumService not initialized')) {
        setNeedsMasterWallet(true);
        return;
      }
      toast.error('Failed to load wallets: ' + msg);
    } finally {
      setLoading(false);
    }
  };

  const loadCreationWalletRow = async () => {
    try {
      if (!currentProjectId) {
        setCreationWalletRow(null);
        return;
      }
      const cw = await apiFetch('/api/me/creation-wallet');
      const addr = (cw?.creationWallet?.address || '').toString().trim();
      if (!addr) {
        setCreationWalletRow(null);
        return;
      }

      await ethereumService.ensureInitialized();
      const provider = ethereumService.provider;
      const [ethBal, tokenBal] = await Promise.all([
        provider.getBalance(addr).catch(() => ethers.BigNumber.from(0)),
        (async () => {
          const tokenAddr = projectTokenAddress;
          if (!tokenAddr || !ethers.utils.isAddress(tokenAddr)) return '0';
          const decRaw = projectSettings?.token_decimals ?? projectSettings?.tokenDecimals;
          const dn = Number(decRaw);
          const decimals = Number.isFinite(dn) && dn >= 0 && dn <= 36 ? dn : (getConfig()?.token?.decimals ?? 18);
          const token = new ethers.Contract(tokenAddr, ['function balanceOf(address) view returns (uint256)'], provider);
          const bal = await token.balanceOf(addr).catch(() => ethers.BigNumber.from(0));
          return ethers.utils.formatUnits(bal, decimals);
        })(),
      ]);

      setCreationWalletRow({
        _kind: 'creation',
        address: addr,
        balance: ethers.utils.formatEther(ethBal),
        tokenBalance: tokenBal,
        buyAmount: '',
        fundAmount: '',
      });
    } catch {
      // keep UI usable
      setCreationWalletRow(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const explorerBase = useMemo(() => {
    const chainId = Number(getConfig()?.network?.chainId || 1);
    if (chainId === 1) return 'https://etherscan.io';
    if (chainId === 11155111) return 'https://sepolia.etherscan.io';
    return 'https://etherscan.io';
  }, []);

  const displayWallets = useMemo(() => {
    const list = Array.isArray(wallets) ? wallets : [];
    return creationWalletRow ? [creationWalletRow, ...list] : list;
  }, [wallets, creationWalletRow]);

  const formatBalance = (balance) => {
    return parseFloat(balance).toFixed(4);
  };

  function safeParseEth(v) {
    const n = typeof v === 'number' ? v : typeof v === 'string' ? parseFloat(v) : NaN;
    return Number.isFinite(n) ? n : 0;
  }

  const requiredEth = useMemo(() => {
    return wallets.reduce((sum, w) => {
      const fund = safeParseEth(w.fundAmount);
      const buy = safeParseEth(w.buyAmount);
      const total = fund > 0 ? fund : buy;
      return sum + total;
    }, 0);
  }, [wallets]);

  const masterEth = useMemo(() => {
    const v = parseFloat(systemWallets.master.balance || '0');
    return Number.isFinite(v) ? v : 0;
  }, [systemWallets.master.balance]);

  const masterDelta = useMemo(() => masterEth - requiredEth, [masterEth, requiredEth]);

  const totalWalletEth = useMemo(() => {
    return wallets.reduce((sum, w) => sum + safeParseEth(w.balance), 0);
  }, [wallets]);

  const refreshAllBalances = async () => {
    await Promise.all([loadWallets(), loadSystemWallets(), loadCreationWalletRow()]);
  };

  const persistWalletEdits = async (nextWallets) => {
    const res = await ethereumService.saveWallets(nextWallets);
    if (res?.success) return;
    toast.warning(`Saved locally (server didn’t persist): ${res?.error || 'unknown error'}`);
  };

  const updateWalletField = (address, patch) => {
    setWallets((prev) => {
      const next = prev.map((w) => (w.address === address ? { ...w, ...patch } : w));
      return next;
    });
  };

  const handleDeleteWallet = (wallet) => {
    setConfirmState({
      type: 'delete',
      wallet,
    });
  };

  const handleFundWallet = (wallet) => {
    if (!wallet?.address) return;
    const fund = safeParseEth(wallet.fundAmount);
    const buy = safeParseEth(wallet.buyAmount);
    const amt = fund > 0 ? fund : buy;
    if (!amt || amt <= 0) {
      toast.error('Set a valid Buy Amount (ETH) first (and optional Fund Amount)');
      return;
    }
    setConfirmState({
      type: 'fund',
      wallet,
      amount: amt,
    });
  };

  const handleOpenActions = (event, wallet) => {
    event.stopPropagation();
    setActionMenu({
      anchorEl: event.currentTarget,
      wallet,
    });
  };

  const handleCloseActions = () => setActionMenu(null);

  const closePrivateKeyExport = () => setPrivateKeyExport(null);

  const openPrivateKeyExport = useCallback(
    async (wallet) => {
      if (!wallet?.address || !walletCanExportPrivateKey(wallet)) {
        toast.error('Private key is not available for this wallet. Try refreshing wallets.');
        return;
      }
      setPrivateKeyExport({ wallet, pk: '', loading: true });
      try {
        let pk = '';
        if (isCreationWallet(wallet)) {
          const data = await apiFetch('/api/me/creation-wallet/export');
          pk = normalizePrivateKeyHex(data?.privateKey);
          if (!pk) throw new Error('No private key returned');
        } else {
          pk = normalizePrivateKeyHex(wallet.privateKey);
          if (!pk) throw new Error('Private key missing — reload wallets from server.');
        }
        setPrivateKeyExport({ wallet, pk, loading: false });
      } catch (e) {
        toast.error(e?.message || 'Could not export private key');
        setPrivateKeyExport(null);
      }
    },
    [apiFetch],
  );

  const handleOpenSendEth = (wallet) => {
    if (!wallet?.address) return;
    setSendEthState({
      wallet,
      amount: '',
      destination: '',
      sending: false,
      maxing: false,
    });
  };

  const handleDistributeAll = async () => {
    const targets = wallets
      .map((w) => {
        const fund = safeParseEth(w.fundAmount);
        const buy = safeParseEth(w.buyAmount);
        const amt = fund > 0 ? fund : buy;
        return { w, amt };
      })
      .filter((x) => x.w?.address && x.amt > 0);

    if (targets.length === 0) {
      return toast.error('No wallets have a valid Buy Amount (ETH) set');
    }

    const total = targets.reduce((sum, x) => sum + x.amt, 0);
    const ok = window.confirm(
      `Distribute ${total.toFixed(6)} ETH from your fund wallet to ${targets.length} wallets?\n\nThis uses each wallet's Fund Amount (or Buy Amount if Fund Amount is empty).`
    );
    if (!ok) return;

    setDistributing(true);
    try {
      const res = await ethereumService.distributeFunds(wallets);
      if (!res?.success) throw new Error(res?.error || 'Distribute failed');
      toast.success(`Distributed ${Number(res?.totalEth || total).toFixed(6)} ETH to ${res?.numberOfTransactions || targets.length} wallets`);
      await refreshAllBalances();
    } catch (e) {
      toast.error(e?.message || 'Failed to distribute funds');
    } finally {
      setDistributing(false);
    }
  };

  const handleSellAllWallets = async () => {
    const pctRaw = (sellAllPercent || '').trim();
    const pct = Number(pctRaw);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      toast.error('Enter a sell percent between 1 and 100.');
      return;
    }
    if (!currentProjectId) {
      toast.error('Select a project first.');
      return;
    }
    if (needsMasterWallet) {
      toast.error('Initialize Ethereum / fund wallet first.');
      return;
    }

    const list = Array.isArray(displayWallets) ? [...displayWallets] : [];
    if (list.length === 0) {
      toast.error('No wallets loaded.');
      return;
    }

    const withTokens = list.filter(hasPositiveTokenBalance);
    if (withTokens.length === 0) {
      toast.error('No wallets have project tokens to sell (0 balance rows are skipped).');
      return;
    }

    const ok = window.confirm(
      `Sell ${pct}% of the project token from each wallet with a balance (${withTokens.length} of ${list.length} row(s))?\n\n` +
        'Wallets with 0 tokens are skipped.\n\n' +
        'Step 1: Approve the router where needed.\n' +
        'Step 2: Swap on each wallet (same % of that wallet’s balance).',
    );
    if (!ok) return;

    setSellingAll(true);
    try {
      await ethereumService.reloadConfig();
      const pctStr = String(pct);

      let creationPk = '';
      if (withTokens.some((w) => isCreationWallet(w))) {
        const exported = await apiFetch('/api/me/creation-wallet/export');
        const pk = (exported?.privateKey || '').toString().trim();
        if (!pk) throw new Error('Creation wallet export failed (cannot bundle approve/sell).');
        creationPk = pk.startsWith('0x') ? pk : `0x${pk}`;
      }

      toast.info(`Step 1/2: Approving via BlockRazor bundle (${withTokens.length} wallet(s) with tokens)…`);
      const approveBundle = await ethereumService.bundleSellApprovePhase(withTokens, pctStr, {
        creationPrivateKey: creationPk,
      });
      if (!approveBundle?.success)
        throw new Error(approveBundle?.error || 'Approve bundle failed');
      if (approveBundle.skippedAll && withTokens.some((w) => !isCreationWallet(w) && w.privateKey))
        toast.info('All wallets already approved for this slice — swaps next.');

      toast.info(`Step 2/2: Selling via BlockRazor bundle…`);
      const swapBundle = await ethereumService.bundleSellSwapPhase(withTokens, pctStr, {
        creationPrivateKey: creationPk,
      });
      if (!swapBundle?.success)
        throw new Error(swapBundle?.error || 'Sell bundle failed');
      if (swapBundle.skippedAll) {
        throw new Error(
          'No swap transactions were bundled (check token balances / allowances vs sell %).',
        );
      }

      toast.success(`Sell‑all bundled on BlockRazor (${swapBundle.count || 0} swap tx(s)).`);
      await refreshAllBalances();
    } catch (e) {
      toast.error(e?.message || 'Sell-all failed');
    } finally {
      setSellingAll(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmState?.wallet) return;
    const target = confirmState.wallet;

    if (confirmState.type === 'delete') {
      const next = wallets.filter((w) => w.address !== target.address);
      setWallets(next);
      await persistWalletEdits(next);
      toast.success('Wallet deleted');
      setConfirmState(null);
      return;
    }

    if (confirmState.type === 'fund') {
      const amt = confirmState.amount;
      setDistributing(true);
      try {
        const res = await ethereumService.distributeFunds([target]);
        if (!res?.success) throw new Error(res?.error || 'Funding failed');
        toast.success(`Funded wallet with ${amt.toFixed(6)} ETH`);
        await refreshAllBalances();
      } catch (e) {
        toast.error(e?.message || 'Failed to fund wallet');
      } finally {
        setDistributing(false);
        setConfirmState(null);
      }
    }
  };

  const headerCellSx = {
    fontWeight: 800,
    fontSize: 12,
    letterSpacing: 0.6,
    color: alpha(theme.palette.common.white, 0.82),
    bgcolor: alpha('#0a0c12', 0.97),
    borderBottom: `1px solid ${alpha('#fff', 0.08)}`,
  };

  const renderWalletTable = () => (
    <TableContainer
      sx={{
        mt: 0,
        maxHeight: 720,
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        borderRadius: 2.5,
        border: `1px solid ${alpha('#fff', 0.1)}`,
        backgroundColor: alpha('#000', 0.28),
      }}
    >
      <Table stickyHeader size="small" sx={{ minWidth: 820 }}>
        <TableHead>
          <TableRow>
            <TableCell width={52} sx={headerCellSx}>
              #
            </TableCell>
            <TableCell sx={headerCellSx}>Address</TableCell>
            <TableCell width={140} sx={headerCellSx}>
              Balance
            </TableCell>
            <TableCell width={120} sx={headerCellSx}>
              Tokens
            </TableCell>
            <TableCell width={150} sx={headerCellSx}>
              Buy (ETH)
            </TableCell>
            <TableCell width={150} sx={headerCellSx}>
              Fund (ETH)
            </TableCell>
            <TableCell align="center" width={200} sx={headerCellSx}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayWallets.map((wallet, idx) => (
            <TableRow
              key={wallet.address}
              hover
              sx={{
                '&:nth-of-type(even) td': { bgcolor: alpha('#fff', 0.02) },
                '& td': { py: 1.35, borderColor: alpha('#fff', 0.06) },
              }}
            >
              <TableCell>
                <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                  {(idx + 1).toString().padStart(2, '0')}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 760 }}>
                    {wallet.address.slice(0, 6)}…{wallet.address.slice(-4)}
                  </Typography>
                  {wallet?._kind === 'creation' ? (
                    <Chip
                      size="small"
                      label="Creation"
                      sx={{
                        height: 22,
                        fontWeight: 750,
                        fontSize: 11,
                        borderRadius: 1.25,
                        color: alpha(theme.palette.secondary.light, 0.98),
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.35)}`,
                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      }}
                    />
                  ) : null}
                  <Tooltip title="Copy">
                    <IconButton size="small" onClick={() => copyToClipboard(wallet.address)} sx={{ border: `1px solid ${alpha('#fff', 0.08)}` }}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatBalance(wallet.balance)} ETH
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 650, color: alpha(theme.palette.secondary.light, 0.95), fontVariantNumeric: 'tabular-nums' }}>
                  {typeof wallet.tokenBalance === 'string' ? wallet.tokenBalance : '0'}
                </Typography>
              </TableCell>
              <TableCell>
                <TextField
                  value={wallet.buyAmount ?? ''}
                  onChange={(e) => updateWalletField(wallet.address, { buyAmount: e.target.value })}
                  onBlur={() => {
                    setWallets((prev) => {
                      persistWalletEdits(prev);
                      return prev;
                    });
                  }}
                  placeholder="0.01"
                  inputProps={{ inputMode: 'decimal' }}
                  size="small"
                  sx={{
                    width: 132,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: alpha('#fff', 0.04),
                    },
                  }}
                  disabled={wallet?._kind === 'creation'}
                />
              </TableCell>
              <TableCell>
                <TextField
                  value={wallet.fundAmount ?? ''}
                  onChange={(e) => updateWalletField(wallet.address, { fundAmount: e.target.value })}
                  onBlur={() => {
                    setWallets((prev) => {
                      persistWalletEdits(prev);
                      return prev;
                    });
                  }}
                  placeholder="0.012"
                  inputProps={{ inputMode: 'decimal' }}
                  size="small"
                  sx={{
                    width: 132,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: alpha('#fff', 0.04),
                    },
                  }}
                  disabled={wallet?._kind === 'creation'}
                />
              </TableCell>
              <TableCell align="center">
                <Button
                  variant="contained"
                  disableElevation
                  onClick={(e) => handleOpenActions(e, wallet)}
                  disabled={distributing || loading || loadingSystem}
                  size="small"
                  sx={{ ...gradientPrimarySx, py: 0.65, minWidth: 104 }}
                >
                  Actions
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderWalletCards = () => (
    <Box sx={{ mt: 2, display: 'grid', gap: 1.5 }}>
      {displayWallets.map((wallet, idx) => (
        <Card
          key={wallet.address}
          variant="outlined"
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
            background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha('#fff', 0.02)} 100%)`,
          }}
        >
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.25 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>
                  {wallet?._kind === 'creation' ? 'CREATION WALLET' : `WALLET ${(idx + 1).toString().padStart(2, '0')}`}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }} noWrap>
                    {wallet.address}
                  </Typography>
                  <Tooltip title="Copy Address">
                    <IconButton size="small" onClick={() => copyToClipboard(wallet.address)}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  BALANCE
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 750 }}>
                  {formatBalance(wallet.balance)} ETH
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  TOKEN BALANCE
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 750 }}>
                  {typeof wallet.tokenBalance === 'string' ? wallet.tokenBalance : '0'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 1.5 }}>
              <TextField
                label="Buy Amount (ETH)"
                value={wallet.buyAmount ?? ''}
                onChange={(e) => updateWalletField(wallet.address, { buyAmount: e.target.value })}
                onBlur={() => {
                  setWallets((prev) => {
                    persistWalletEdits(prev);
                    return prev;
                  });
                }}
                placeholder="0.01"
                inputProps={{ inputMode: 'decimal' }}
                size="small"
                fullWidth
              />
              <TextField
                label="Fund Amount (ETH)"
                value={wallet.fundAmount ?? ''}
                onChange={(e) => updateWalletField(wallet.address, { fundAmount: e.target.value })}
                onBlur={() => {
                  setWallets((prev) => {
                    persistWalletEdits(prev);
                    return prev;
                  });
                }}
                placeholder="0.012"
                inputProps={{ inputMode: 'decimal' }}
                size="small"
                fullWidth
              />
            </Box>

            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="contained"
                disableElevation
                onClick={(e) => handleOpenActions(e, wallet)}
                disabled={distributing || loading || loadingSystem}
                fullWidth
                sx={{ ...gradientPrimarySx, py: 1 }}
              >
                Actions
              </Button>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
      <Box sx={{ mb: 2.25 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              letterSpacing: 0.6,
              backgroundImage: `linear-gradient(90deg, ${accentPurple} 0%, #6366f1 45%, ${accentCyan} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Wallet management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 620 }}>
            Create bundle wallets, set buy / fund ETH per row, distribute from your fund wallet, then use Bundle Launch.
          </Typography>
      </Box>

      <MotionPaper
        elevation={0}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        sx={{
          mb: 2,
          borderRadius: 4,
          border: `1px solid ${alpha(accentPurple, 0.22)}`,
          background: `linear-gradient(165deg, ${alpha(accentPurple, 0.065)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 50%, ${alpha(accentCyan, 0.045)} 100%)`,
          backdropFilter: 'blur(12px)',
        }}
      >
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ p: { xs: 2, sm: 2.25 }, alignItems: 'stretch' }}>
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <Box
            sx={{
              minWidth: 0,
              width: '100%',
              borderRadius: 4,
              p: '1px',
              background:
                'linear-gradient(135deg, rgba(168,85,247,0.55), rgba(99,102,241,0.35), rgba(34,211,238,0.25))',
            }}
          >
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                height: '100%',
                border: '1px solid rgba(255,255,255,0.08)',
                background:
                  'radial-gradient(1200px 400px at 0% 0%, rgba(168,85,247,0.14), transparent 60%), radial-gradient(900px 300px at 100% 0%, rgba(34,211,238,0.10), transparent 55%), rgba(9,10,16,0.65)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <CardContent sx={{ py: 2.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2 }}>
                      FUND WALLET
                    </Typography>

                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        mt: 0.75,
                        px: 1,
                        py: 0.5,
                        borderRadius: 999,
                        border: '1px solid rgba(255,255,255,0.10)',
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        maxWidth: '100%',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 800 }} noWrap>
                        {systemWallets.master.address
                          ? `${systemWallets.master.address.slice(0, 6)}...${systemWallets.master.address.slice(-4)}`
                          : 'Not set'}
                      </Typography>
                      {systemWallets.master.address ? (
                        <Tooltip title="Copy address">
                          <IconButton size="small" onClick={() => copyToClipboard(systemWallets.master.address)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Refresh balances">
                      <IconButton
                        onClick={refreshAllBalances}
                        disabled={loading || loadingSystem}
                        size="small"
                        sx={{
                          border: '1px solid rgba(255,255,255,0.10)',
                          borderRadius: 2.25,
                          backgroundColor: 'rgba(255,255,255,0.02)',
                        }}
                      >
                        {loading || loadingSystem ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    {systemWallets.master.address ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleOpenMasterQr}
                        disabled={loading || loadingSystem}
                        startIcon={<QrCode2Icon />}
                        sx={{
                          borderRadius: 999,
                          px: 1.25,
                          py: 0.75,
                          minWidth: 0,
                          textTransform: 'none',
                          fontWeight: 850,
                          borderColor: 'rgba(255,255,255,0.14)',
                          backgroundColor: 'rgba(255,255,255,0.02)',
                          color: 'rgba(233, 231, 255, 0.95)',
                          '&:hover': {
                            borderColor: 'rgba(168, 85, 247, 0.40)',
                            backgroundColor: 'rgba(168, 85, 247, 0.08)',
                          },
                        }}
                      >
                        Send
                      </Button>
                    ) : null}
                  </Box>
                </Box>

                {needsMasterWallet ? (
                  <Typography variant="body2" color="warning.main" sx={{ mt: 1.5 }}>
                    Create or import a fund wallet first.
                  </Typography>
                ) : (
                  <>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 2 }}>
                        BALANCE
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 950,
                          mt: 0.75,
                          letterSpacing: 0.2,
                        }}
                      >
                        {systemWallets.master.balance ? `${formatBalance(systemWallets.master.balance)} ETH` : '—'}
                      </Typography>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 2 }}>
                        FUNDING SUMMARY
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 1.25,
                        }}
                      >
                        <Box
                          sx={{
                            borderRadius: 3,
                            p: 1.25,
                            border: '1px solid rgba(255,255,255,0.08)',
                            backgroundColor: 'rgba(255,255,255,0.02)',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
                            REQUIRED
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 900, mt: 0.25 }}>
                            {requiredEth.toFixed(6)} ETH
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderRadius: 3,
                            p: 1.25,
                            border: '1px solid rgba(255,255,255,0.08)',
                            backgroundColor: 'rgba(255,255,255,0.02)',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
                            {masterDelta >= 0 ? 'AVAILABLE' : 'SHORTFALL'}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 950,
                              mt: 0.25,
                              color: masterDelta >= 0 ? 'success.main' : 'error.main',
                            }}
                          >
                            {(masterDelta >= 0 ? masterDelta : Math.abs(masterDelta)).toFixed(6)} ETH
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
          </Box>

          <Stack spacing={2} sx={{ width: { xs: '100%', lg: 268 }, flexShrink: 0 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: 2,
                border: `1px solid ${alpha(accentPurple, 0.26)}`,
                bgcolor: alpha(accentPurple, 0.07),
              }}
            >
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2, fontWeight: 750 }}>
                BUNDLE WALLETS
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 950, mt: 0.35 }}>
                {wallets.length}
              </Typography>
            </Paper>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: 2,
                border: `1px solid ${alpha(accentCyan, 0.26)}`,
                bgcolor: alpha(accentCyan, 0.08),
              }}
            >
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2, fontWeight: 750 }}>
                SUM ETH (ROWS)
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.35, fontVariantNumeric: 'tabular-nums' }}>
                {totalWalletEth.toFixed(4)} ETH
              </Typography>
            </Paper>
          </Stack>
        </Stack>
      </MotionPaper>

          <Dialog
            open={masterQrOpen}
            onClose={() => (masterSending ? null : setMasterQrOpen(false))}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 900 }}>
              Fund wallet
            </DialogTitle>
            <DialogContent dividers sx={{ pt: 2 }}>
              <Box sx={{ display: 'grid', gap: 2 }}>
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                    FUND THIS ADDRESS
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 800 }}>
                      {systemWallets.master.address || '—'}
                    </Typography>
                    {systemWallets.master.address ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => copyToClipboard(systemWallets.master.address)}
                        startIcon={<CopyIcon fontSize="small" />}
                        sx={{ borderRadius: 999 }}
                      >
                        Copy
                      </Button>
                    ) : null}
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                    <Box sx={{ background: '#fff', p: 1.5, borderRadius: 3, boxShadow: '0 10px 30px rgba(0,0,0,0.35)' }}>
                      <QRCodeCanvas value={systemWallets.master.address || ''} size={240} />
                    </Box>
                  </Box>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                    SEND FROM FUND WALLET
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Transfers ETH from the fund wallet to a destination address.
                  </Typography>

                  <Box sx={{ display: 'grid', gap: 1.25, mt: 1.25 }}>
                    <TextField
                      label="Destination address"
                      value={masterSendTo}
                      onChange={(e) => setMasterSendTo(e.target.value)}
                      placeholder="0x..."
                      fullWidth
                      size="small"
                      disabled={masterSending}
                      error={masterSendToOk === false}
                      helperText={masterSendToOk === false ? 'Invalid address' : ' '}
                    />
                    <TextField
                      label="Amount"
                      value={masterSendAmount}
                      onChange={(e) => setMasterSendAmount(e.target.value)}
                      placeholder="0.01"
                      fullWidth
                      size="small"
                      disabled={masterSending}
                      inputProps={{ inputMode: 'decimal' }}
                      error={masterSendAmountOk === false}
                      helperText={masterSendAmountOk === false ? 'Enter a valid amount' : ' '}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ gap: 0.75 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={handleMasterMax}
                              disabled={masterSending || masterMaxing}
                              sx={{ fontWeight: 900, borderRadius: 999, minWidth: 0, px: 1 }}
                            >
                              {masterMaxing ? <CircularProgress size={14} /> : 'MAX'}
                            </Button>
                            <Typography variant="caption" sx={{ opacity: 0.75 }}>
                              ETH
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setMasterQrOpen(false)} disabled={masterSending} sx={{ borderRadius: 999 }}>
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={masterSending ? <CircularProgress size={16} /> : <CallMadeIcon />}
                onClick={handleSendFromMaster}
                disabled={
                  masterSending ||
                  !systemWallets.master.address ||
                  masterSendToOk !== true ||
                  masterSendAmountOk !== true
                }
                sx={{ borderRadius: 999 }}
              >
                {masterSending ? 'Sending…' : 'Send'}
              </Button>
            </DialogActions>
          </Dialog>

      <MotionPaper
        elevation={0}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        sx={{
          borderRadius: 4,
          mb: 0,
          border: `1px solid ${alpha(accentPurple, 0.18)}`,
          background: `linear-gradient(165deg, ${alpha(accentPurple, 0.045)} 0%, ${alpha(theme.palette.background.paper, 0.85)} 40%, transparent 100%)`,
          backdropFilter: 'blur(12px)',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 880, mb: 0.25 }}>
            Wallet roster
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Edits autosave per row — used by Bundle Launch for buy sizing.
          </Typography>
          {isNarrow ? renderWalletCards() : renderWalletTable()}

          <Box
            sx={{
              mt: 3,
              p: { xs: 2, sm: 2.25 },
              borderRadius: 3,
              border: `1px solid ${alpha(accentCyan, 0.22)}`,
              background: alpha(accentCyan, 0.04),
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ flexGrow: 1, minWidth: 200 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 880 }}>
                Distribute funds
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                Sends each wallet’s <strong>Fund amount (ETH)</strong> from the fund wallet above.
              </Typography>
            </Box>
            <Button
              variant="contained"
              disableElevation
              onClick={handleDistributeAll}
              disabled={distributing || loading || loadingSystem || needsMasterWallet}
              size="medium"
              sx={{ ...gradientPrimarySx, px: 3, py: 1 }}
            >
              {distributing ? 'Distributing…' : 'Distribute funds'}
            </Button>
          </Box>

          <Card
            variant="outlined"
            sx={{
              mt: 3,
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid rgba(148, 163, 184, 0.2)',
              background:
                'linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(30, 27, 58, 0.5) 45%, rgba(15, 23, 42, 0.65) 100%)',
              boxShadow: '0 20px 48px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
            }}
          >
            <CardContent sx={{ p: { xs: 2.25, sm: 2.75 }, '&:last-child': { pb: { xs: 2.25, sm: 2.75 } } }}>
              <Stack spacing={2.25}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={{ xs: 2, md: 3 }}
                  alignItems={{ xs: 'stretch', md: 'flex-start' }}
                  justifyContent="space-between"
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                      <Chip
                        size="small"
                        label="Approve → swap"
                        sx={{
                          height: 24,
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: 0.3,
                          bgcolor: 'rgba(34, 211, 238, 0.1)',
                          color: 'rgba(204, 251, 241, 0.95)',
                          border: '1px solid rgba(34, 211, 238, 0.25)',
                        }}
                      />
                      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.4, lineHeight: 1.2 }}>
                        Creation + bundle
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          background: 'linear-gradient(145deg, rgba(34, 211, 238, 0.2), rgba(99, 102, 241, 0.15))',
                          border: '1px solid rgba(148, 163, 184, 0.2)',
                        }}
                      >
                        <TrendingDownIcon sx={{ fontSize: 22, color: 'rgba(94, 234, 212, 0.95)' }} />
                      </Box>
                      <Typography
                        component="h3"
                        variant="h6"
                        sx={{
                          fontWeight: 800,
                          letterSpacing: 0.2,
                          lineHeight: 1.25,
                          background: 'linear-gradient(90deg, #f8fafc 0%, #a5f3fc 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                        }}
                      >
                        Sell from all wallets
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, maxWidth: 520 }}>
                      Enter one percentage — it applies to each wallet’s project-token balance. Wallets with{' '}
                      <Box component="span" sx={{ color: 'rgba(226, 232, 240, 0.88)', fontWeight: 600 }}>
                        zero balance are skipped
                      </Box>
                      . Approvals run first, then swaps.
                    </Typography>
                  </Box>

                  <Divider
                    flexItem
                    orientation="vertical"
                    sx={{ display: { xs: 'none', md: 'block' }, borderColor: 'rgba(148, 163, 184, 0.12)' }}
                  />

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', sm: 'flex-end' }}
                    sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' }, pt: { md: 0.5 } }}
                  >
                    <TextField
                      label="Amount per wallet"
                      value={sellAllPercent}
                      onChange={(e) => setSellAllPercent(e.target.value)}
                      type="number"
                      size="small"
                      inputProps={{ min: 1, max: 100, step: 1 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                              %
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: { xs: '100%', sm: 152 },
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: 'rgba(2, 6, 23, 0.45)',
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={handleSellAllWallets}
                      disabled={
                        sellingAll ||
                        distributing ||
                        loading ||
                        loadingSystem ||
                        needsMasterWallet ||
                        !currentProjectId
                      }
                      sx={{
                        minHeight: 40,
                        px: 2.5,
                        borderRadius: 2,
                        fontWeight: 800,
                        letterSpacing: 0.6,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        background: 'linear-gradient(135deg, #5eead4 0%, #38bdf8 55%, #818cf8 100%)',
                        color: '#020617',
                        boxShadow: '0 12px 32px rgba(56, 189, 248, 0.22)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #99f6e4 0%, #7dd3fc 50%, #a5b4fc 100%)',
                          boxShadow: '0 14px 36px rgba(56, 189, 248, 0.3)',
                        },
                        '&.Mui-disabled': {
                          background: sellingAll
                            ? 'linear-gradient(135deg, rgba(94, 234, 212, 0.55), rgba(129, 140, 248, 0.45))'
                            : 'rgba(148, 163, 184, 0.2)',
                          color: sellingAll ? 'rgba(2, 6, 23, 0.65)' : 'rgba(148, 163, 184, 0.5)',
                        },
                      }}
                    >
                      {sellingAll ? (
                        <>
                          <CircularProgress size={18} sx={{ mr: 1, color: '#020617' }} />
                          Selling…
                        </>
                      ) : (
                        'Run sell all'
                      )}
                    </Button>
                  </Stack>
                </Stack>
                <Typography
                  variant="caption"
                  component="div"
                  color="text.secondary"
                  sx={{
                    pt: 2,
                    mt: 0,
                    borderTop: '1px solid rgba(148, 163, 184, 0.08)',
                    opacity: 0.75,
                  }}
                >
                  Uses router + token from Configuration. Each wallet needs ETH for gas.
                </Typography>
              </Stack>
            </CardContent>
          </Card>

        </Box>
      </MotionPaper>
      <Menu
        anchorEl={actionMenu?.anchorEl || null}
        open={Boolean(actionMenu)}
        onClose={handleCloseActions}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            mt: 1,
            px: 0.5,
            py: 0.5,
            borderRadius: 2,
            minWidth: 168,
            bgcolor: 'rgba(10,10,20,0.98)',
            border: '1px solid rgba(148,163,184,0.25)',
            boxShadow:
              '0 18px 45px rgba(15,23,42,0.85), 0 0 0 1px rgba(15,23,42,0.9), 0 0 40px rgba(94,234,212,0.12)',
          },
        }}
        MenuListProps={{
          sx: {
            py: 0,
            '& .MuiMenuItem-root': {
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: 0.4,
              px: 2,
              py: 1,
              borderRadius: 1.5,
              mx: 0.25,
              my: 0.25,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleCloseActions();
            if (actionMenu?.wallet) handleFundWallet(actionMenu.wallet);
          }}
          disabled={distributing || loading || loadingSystem}
          sx={{
            color: 'rgba(248,250,252,0.96)',
            '&:hover': {
              bgcolor: 'rgba(59,130,246,0.2)',
            },
          }}
        >
          Fund
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseActions();
            if (actionMenu?.wallet) handleOpenSendEth(actionMenu.wallet);
          }}
          sx={{
            color: 'rgba(226,232,240,0.92)',
            '&:hover': {
              bgcolor: 'rgba(59,130,246,0.18)',
            },
          }}
        >
          Send ETH
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseActions();
            if (actionMenu?.wallet) {
              setTransferState({
                wallet: actionMenu.wallet,
                mode: 'amount',
                percent: '25',
                amount: '',
                destination: '',
              });
            }
          }}
          sx={{
            color: 'rgba(226,232,240,0.9)',
            '&:hover': {
              bgcolor: 'rgba(52,211,153,0.22)',
            },
          }}
        >
          Transfer token
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseActions();
            if (actionMenu?.wallet) {
              setSellState({
                wallet: actionMenu.wallet,
                mode: 'percent',
                percent: '25',
                amount: '',
                txs: null,
              });
            }
          }}
          sx={{
            color: 'rgba(226,232,240,0.85)',
            '&:hover': {
              bgcolor: 'rgba(129,140,248,0.22)',
            },
          }}
        >
          Sell
        </MenuItem>
        <MenuItem
          onClick={() => {
            const w = actionMenu?.wallet;
            handleCloseActions();
            if (w) openPrivateKeyExport(w);
          }}
          disabled={!walletCanExportPrivateKey(actionMenu?.wallet)}
          sx={{
            color: 'rgba(253,224,71,0.95)',
            '&:hover': {
              bgcolor: 'rgba(250,204,21,0.12)',
            },
            '&.Mui-disabled': { opacity: 0.45 },
          }}
        >
          Export private key
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseActions();
            if (actionMenu?.wallet) handleDeleteWallet(actionMenu.wallet);
          }}
          sx={{
            color: 'error.main',
            '&:hover': {
              bgcolor: 'rgba(248,113,113,0.18)',
            },
          }}
          disabled={distributing || loading || loadingSystem}
        >
          Delete
        </MenuItem>
      </Menu>
      <Dialog
        open={!!sendEthState}
        onClose={() => (sendEthState?.sending ? null : setSendEthState(null))}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(6,10,25,0.98)',
            borderRadius: 3,
            border: '1px solid rgba(148,163,184,0.35)',
            boxShadow:
              '0 24px 80px rgba(15,23,42,0.95), 0 0 0 1px rgba(15,23,42,0.9), 0 0 50px rgba(94,234,212,0.18)',
          },
        }}
      >
        {sendEthState && (
          <>
            <DialogTitle sx={{ fontWeight: 800 }}>Send ETH</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sends ETH from this wallet using the bundler (MEV-Share).
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  FROM WALLET
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {sendEthState.wallet.address}
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', rowGap: 2 }}>
                <TextField
                  label="Amount"
                  value={sendEthState.amount}
                  onChange={(e) =>
                    setSendEthState((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }))
                  }
                  placeholder="0.01"
                  fullWidth
                  size="small"
                  inputProps={{ inputMode: 'decimal' }}
                  disabled={sendEthState.sending}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ gap: 0.75 }}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={async () => {
                            setSendEthState((p) => ({ ...p, maxing: true }));
                            try {
                              await ethereumService.ensureInitialized();
                              const provider = ethereumService.provider;
                              const bal = await provider.getBalance(sendEthState.wallet.address);
                              const fee = await provider.getFeeData().catch(() => null);
                              const gasLimit = ethers.BigNumber.from(21000);
                              const gasPrice = fee?.maxFeePerGas || fee?.gasPrice || ethers.utils.parseUnits('2', 'gwei');
                              const gasCost = gasLimit.mul(gasPrice);
                              const maxWei = bal.gt(gasCost) ? bal.sub(gasCost) : ethers.BigNumber.from(0);
                              const maxEth = Number(ethers.utils.formatEther(maxWei));
                              const buffered = Math.max(0, maxEth - 0.00001);
                              setSendEthState((p) => ({ ...p, amount: buffered.toFixed(6) }));
                            } catch (e) {
                              toast.error(e?.message || 'Failed to calculate max');
                            } finally {
                              setSendEthState((p) => ({ ...p, maxing: false }));
                            }
                          }}
                          disabled={sendEthState.sending || sendEthState.maxing}
                          sx={{ fontWeight: 900, borderRadius: 999, minWidth: 0, px: 1 }}
                        >
                          {sendEthState.maxing ? <CircularProgress size={14} /> : 'MAX'}
                        </Button>
                        <Typography variant="caption" sx={{ opacity: 0.75 }}>
                          ETH
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Destination address"
                  value={sendEthState.destination}
                  onChange={(e) =>
                    setSendEthState((prev) => ({
                      ...prev,
                      destination: e.target.value,
                    }))
                  }
                  placeholder="0x..."
                  fullWidth
                  size="small"
                  disabled={sendEthState.sending}
                  inputProps={{ spellCheck: 'false' }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setSendEthState(null)} variant="text" disabled={sendEthState.sending}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={sendEthState.sending}
                onClick={async () => {
                  const amt = (sendEthState.amount || '').trim();
                  const dest = (sendEthState.destination || '').trim();
                  if (!amt || !Number.isFinite(Number(amt)) || Number(amt) <= 0) {
                    toast.error('Enter a valid amount.');
                    return;
                  }
                  if (!ethers.utils.isAddress(dest)) {
                    toast.error('Enter a valid destination address.');
                    return;
                  }
                  if (!sendEthState.wallet?.privateKey) {
                    toast.error('Missing private key for this wallet.');
                    return;
                  }

                  setSendEthState((p) => ({ ...p, sending: true }));
                  try {
                    await ethereumService.ensureInitialized();
                    const provider = ethereumService.provider;
                    const fee = await provider.getFeeData().catch(() => null);
                    const network = await provider.getNetwork();
                    const senderPk = sendEthState.wallet.privateKey.startsWith('0x')
                      ? sendEthState.wallet.privateKey
                      : `0x${sendEthState.wallet.privateKey}`;
                    const signer = new ethers.Wallet(senderPk, provider);
                    const nonce = await signer.getTransactionCount('pending');
                    const value = ethers.utils.parseEther(amt);
                    const use1559 = !!fee?.maxFeePerGas;
                    const txReq = {
                      to: dest,
                      value,
                      gasLimit: 21000,
                      chainId: network.chainId,
                      nonce,
                      ...(use1559 ? { type: 2 } : { type: 0 }),
                      ...(use1559 ? { maxFeePerGas: fee.maxFeePerGas } : {}),
                      ...(use1559 && fee?.maxPriorityFeePerGas ? { maxPriorityFeePerGas: fee.maxPriorityFeePerGas } : {}),
                      ...(!use1559 && fee?.gasPrice ? { gasPrice: fee.gasPrice } : {}),
                    };
                    const signedTx = await signer.signTransaction(txReq);
                    const relayRes = await ethereumService.sendSignedTransactionsViaFlashbots([signedTx]);
                    const bundleHash = relayRes?.bundleHash || relayRes?.result?.bundleHash || relayRes?.result?.bundle_hash || null;
                    toast.success(bundleHash ? `Bundle submitted: ${String(bundleHash).slice(0, 12)}…` : 'Submitted');
                    setSendEthState(null);
                    await refreshAllBalances();
                  } catch (e) {
                    toast.error(e?.message || 'Failed to send');
                    setSendEthState((p) => ({ ...p, sending: false }));
                  }
                }}
              >
                {sendEthState.sending ? 'Sending…' : 'Send'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Dialog
        open={!!confirmState}
        onClose={() => (distributing ? null : setConfirmState(null))}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(6,10,25,0.98)',
            borderRadius: 3,
            border: '1px solid rgba(148,163,184,0.35)',
            boxShadow:
              '0 24px 80px rgba(15,23,42,0.95), 0 0 0 1px rgba(15,23,42,0.9), 0 0 50px rgba(94,234,212,0.18)',
          },
        }}
      >
        {confirmState && (
          <>
            <DialogTitle sx={{ fontWeight: 800 }}>
              {confirmState.type === 'delete' ? 'Delete wallet' : 'Fund wallet'}
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {confirmState.type === 'delete'
                    ? 'This will remove this wallet from the current project. The address will no longer appear in Wallet Management.'
                    : 'You are about to fund this wallet from your fund wallet.'}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'grid', rowGap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    WALLET
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {confirmState.wallet.address}
                  </Typography>
                </Box>
                {confirmState.type === 'fund' && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      AMOUNT
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      {confirmState.amount.toFixed(6)} ETH
                    </Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                onClick={() => setConfirmState(null)}
                disabled={distributing}
                variant="text"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmAction}
                disabled={distributing}
                color={confirmState.type === 'delete' ? 'error' : 'primary'}
                variant="contained"
              >
                {confirmState.type === 'delete' ? 'Delete wallet' : 'Confirm funding'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Dialog
        open={!!transferState}
        onClose={() => setTransferState(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(6,10,25,0.98)',
            borderRadius: 3,
            border: '1px solid rgba(148,163,184,0.35)',
            boxShadow:
              '0 24px 80px rgba(15,23,42,0.95), 0 0 0 1px rgba(15,23,42,0.9), 0 0 50px rgba(94,234,212,0.18)',
          },
        }}
      >
        {transferState && (
          <>
            <DialogTitle sx={{ fontWeight: 800 }}>
              Transfer token
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter how many tokens you want to move from this wallet and the destination address.
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  FROM WALLET
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {transferState.wallet.address}
                </Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  AVAILABLE
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {typeof transferState.wallet.tokenBalance === 'string'
                    ? transferState.wallet.tokenBalance
                    : '0'}{' '}
                  tokens
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', rowGap: 2 }}>
                <ToggleButtonGroup
                  value={transferState.mode || 'amount'}
                  exclusive
                  onChange={(_, v) => v && setTransferState((prev) => ({ ...prev, mode: v }))}
                  size="small"
                >
                  <ToggleButton value="percent">Percent</ToggleButton>
                  <ToggleButton value="amount">Amount</ToggleButton>
                </ToggleButtonGroup>

                {transferState.mode === 'percent' ? (
                  <TextField
                    label="Transfer %"
                    value={transferState.percent || '25'}
                    onChange={(e) =>
                      setTransferState((prev) => ({
                        ...prev,
                        percent: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 1 }}
                  />
                ) : null}
                <TextField
                  label="Token amount"
                  value={transferState.amount}
                  onChange={(e) =>
                    setTransferState((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }))
                  }
                  placeholder="100000"
                  fullWidth
                  size="small"
                  inputProps={{ inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          onClick={() =>
                            setTransferState((prev) => ({
                              ...prev,
                              amount:
                                typeof prev.wallet.tokenBalance === 'string'
                                  ? prev.wallet.tokenBalance
                                  : '',
                            }))
                          }
                          sx={{
                            textTransform: 'none',
                            fontSize: 11,
                            px: 1.2,
                          }}
                        >
                          Max
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Destination address"
                  value={transferState.destination}
                  onChange={(e) =>
                    setTransferState((prev) => ({
                      ...prev,
                      destination: e.target.value,
                    }))
                  }
                  placeholder="0x..."
                  fullWidth
                  size="small"
                  inputProps={{ spellCheck: 'false' }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setTransferState(null)} variant="text">
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const dest = (transferState.destination || '').trim();
                  const mode = transferState.mode || 'amount';
                  const amt = (transferState.amount || '').trim();
                  const pct = (transferState.percent || '').trim();
                  if (mode === 'amount' && !amt) {
                    toast.error('Enter a token amount.');
                    return;
                  }
                  if (mode === 'percent' && !pct) {
                    toast.error('Enter a percent.');
                    return;
                  }
                  if (!dest || !dest.startsWith('0x') || dest.length < 16) {
                    toast.error('Enter a valid destination address.');
                    return;
                  }
                  setLoading(true);
                  try {
                    await ethereumService.reloadConfig();
                    const w = transferState.wallet;
                    let res = null;
                    if (isCreationWallet(w)) {
                      const cfg = getConfig();
                      const rpcUrl = (cfg?.network?.rpcUrl || '').toString().trim();
                      const tokenAddress = projectTokenAddress;
                      if (!rpcUrl) throw new Error('RPC URL is not configured');
                      if (!tokenAddress) throw new Error('Token address is not set (assign/deploy in Launch Wizard)');
                      res = await apiFetch('/api/me/token/transfer', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          rpcUrl,
                          tokenAddress,
                          to: dest,
                          percent: mode === 'percent' ? pct : null,
                          amountTokens: mode === 'amount' ? amt : null,
                        }),
                      });
                      if (!res?.txHash) throw new Error(res?.error || 'Transfer failed');
                      toast.success(`Transfer sent: ${String(res.txHash).slice(0, 12)}…`);
                    } else {
                      if (!w?.privateKey) throw new Error('Wallet private key is missing.');
                      res = await ethereumService.transferTokensAuto({
                        walletPrivateKey: w.privateKey,
                        walletAddress: w.address,
                        to: dest,
                        percent: mode === 'percent' ? pct : null,
                        amountTokens: mode === 'amount' ? amt : null,
                      });
                      if (!res?.success) throw new Error(res?.error || 'Transfer failed');
                      toast.success(res?.txHash ? `Transfer sent: ${String(res.txHash).slice(0, 12)}…` : 'Transfer sent');
                    }
                    setTransferState(null);
                    await loadWallets();
                    await loadCreationWalletRow();
                  } catch (e) {
                    toast.error(e?.message || 'Transfer failed');
                  } finally {
                    setLoading(false);
                  }
                }}
                variant="contained"
                disabled={loading}
              >
                {loading ? <CircularProgress size={18} /> : 'Confirm transfer'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={!!sellState}
        onClose={() => (loading ? null : setSellState(null))}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(6,10,25,0.98)',
            borderRadius: 3,
            border: '1px solid rgba(148,163,184,0.35)',
            boxShadow:
              '0 24px 80px rgba(15,23,42,0.95), 0 0 0 1px rgba(15,23,42,0.9), 0 0 50px rgba(94,234,212,0.18)',
          },
        }}
      >
        {sellState && (
          <>
            <DialogTitle sx={{ fontWeight: 800 }}>Sell tokens</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Approve is automatic (if needed), then swaps tokens to ETH.
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  FROM WALLET
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {sellState.wallet.address}
                </Typography>
              </Box>

              <ToggleButtonGroup
                value={sellState.mode || 'percent'}
                exclusive
                onChange={(_, v) => v && setSellState((prev) => ({ ...prev, mode: v }))}
                size="small"
                sx={{ mb: 2 }}
              >
                <ToggleButton value="percent">Percent</ToggleButton>
                <ToggleButton value="amount">Amount</ToggleButton>
              </ToggleButtonGroup>

              {sellState.mode === 'percent' ? (
                <TextField
                  label="Sell %"
                  value={sellState.percent || '25'}
                  onChange={(e) => setSellState((prev) => ({ ...prev, percent: e.target.value }))}
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 1 }}
                />
              ) : (
                <TextField
                  label="Token amount"
                  value={sellState.amount}
                  onChange={(e) => setSellState((prev) => ({ ...prev, amount: e.target.value }))}
                  placeholder="100000"
                  fullWidth
                  size="small"
                  inputProps={{ inputMode: 'decimal' }}
                />
              )}

              {sellState.txs ? (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {sellState.txs.approveHash ? (
                    <div>
                      Approve:{' '}
                      <a href={`${explorerBase}/tx/${sellState.txs.approveHash}`} target="_blank" rel="noreferrer">
                        {sellState.txs.approveHash.slice(0, 18)}…
                      </a>
                    </div>
                  ) : null}
                  {sellState.txs.swapHash ? (
                    <div>
                      Swap:{' '}
                      <a href={`${explorerBase}/tx/${sellState.txs.swapHash}`} target="_blank" rel="noreferrer">
                        {sellState.txs.swapHash.slice(0, 18)}…
                      </a>
                    </div>
                  ) : null}
                </Alert>
              ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setSellState(null)} variant="text" disabled={loading}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const mode = sellState.mode || 'percent';
                  const amt = (sellState.amount || '').trim();
                  const pct = (sellState.percent || '').trim();
                  if (mode === 'amount' && !amt) {
                    toast.error('Enter a token amount.');
                    return;
                  }
                  if (mode === 'percent' && !pct) {
                    toast.error('Enter a percent.');
                    return;
                  }
                  setLoading(true);
                  try {
                    await ethereumService.reloadConfig();
                    const w = sellState.wallet;
                    if (isCreationWallet(w)) {
                      const cfg = getConfig();
                      const rpcUrl = (cfg?.network?.rpcUrl || '').toString().trim();
                      const tokenAddress = projectTokenAddress;
                      const routerAddress = (cfg?.network?.routerAddress || '').toString().trim();
                      if (!rpcUrl) throw new Error('RPC URL is not configured');
                      if (!tokenAddress) throw new Error('Token address is not set (assign/deploy in Launch Wizard)');
                      if (!routerAddress) throw new Error('Router address is not set');
                      const res = await apiFetch('/api/me/token/sell', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          rpcUrl,
                          tokenAddress,
                          routerAddress,
                          percent: mode === 'percent' ? pct : null,
                          amountTokens: mode === 'amount' ? amt : null,
                        }),
                      });
                      setSellState((prev) => ({
                        ...prev,
                        txs: { approveHash: res?.approveHash || null, swapHash: res?.swapHash || null },
                      }));
                      toast.success(res?.swapHash ? `Sell sent: ${String(res.swapHash).slice(0, 12)}…` : 'Sell sent');
                    } else {
                      if (!w?.privateKey) throw new Error('Wallet private key is missing.');
                      const res = await ethereumService.sellTokensAuto({
                        walletPrivateKey: w.privateKey,
                        walletAddress: w.address,
                        percent: mode === 'percent' ? pct : null,
                        amountTokens: mode === 'amount' ? amt : null,
                      });
                      if (!res?.success) throw new Error(res?.error || 'Sell failed');
                      setSellState((prev) => ({
                        ...prev,
                        txs: { approveHash: res?.approveHash || null, swapHash: res?.swapHash || null },
                      }));
                      toast.success(res?.swapHash ? `Sell sent: ${String(res.swapHash).slice(0, 12)}…` : 'Sell sent');
                    }
                    await loadWallets();
                    await loadCreationWalletRow();
                  } catch (e) {
                    toast.error(e?.message || 'Sell failed');
                  } finally {
                    setLoading(false);
                  }
                }}
                variant="contained"
                disabled={loading}
              >
                {loading ? <CircularProgress size={18} /> : 'Sell now'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={!!privateKeyExport}
        onClose={privateKeyExport?.loading ? undefined : closePrivateKeyExport}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(6,10,25,0.98)',
            borderRadius: 3,
            border: '1px solid rgba(148,163,184,0.35)',
          },
        }}
      >
        {privateKeyExport ? (
          <>
            <DialogTitle sx={{ fontWeight: 800 }}>Export private key</DialogTitle>
            <DialogContent dividers>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Never share this key. Anyone with it can move all funds from this wallet. Prefer cold storage or a
                hardware wallet once exported.
              </Alert>
              <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
                ADDRESS
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mb: 2 }}>
                {privateKeyExport?.wallet?.address || '—'}
              </Typography>
              {privateKeyExport.loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
                    PRIVATE KEY
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    value={privateKeyExport.pk || ''}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        mt: 0.5,
                        fontFamily: 'monospace',
                        fontSize: 13,
                        wordBreak: 'break-all',
                      },
                    }}
                    size="small"
                  />
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
              <Button variant="text" onClick={closePrivateKeyExport} disabled={privateKeyExport.loading}>
                Close
              </Button>
              <Button
                variant="contained"
                color="warning"
                startIcon={<CopyIcon />}
                disabled={privateKeyExport.loading || !privateKeyExport.pk}
                onClick={() => copyToClipboard(privateKeyExport.pk)}
              >
                Copy key
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </Box>
  );
};

export default WalletManagement; 